/**
 * The boot screen and every failure that can stop the page before it starts
 * (BootScreen.tsx, engine/runtime.ts, api.ts, terminal.ts).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## The failure sentences are the important half
 *
 * Everything under `failure` is read by somebody whose download just died or
 * whose browser cannot run a VM, and it is the only writing in the product
 * whose job is to stop them closing the tab. So each one says what happened
 * and what to do about it, in that order, and where there is nothing to do it
 * says that rather than apologising. The Arabic is written to the same brief:
 * plain, specific, no `عذرًا`, no padding. The English does not say "Oops"
 * and neither does the Arabic.
 *
 * ## Where the translated half stops
 *
 * `toFailure` in App.tsx hands the screen a `{ code, detail }`, where `detail`
 * is the raw message from `@engineer-os/vm` or from a thrown `Error` —
 * `the saved state was produced by a different build — rootfs.img`. That is
 * developer text: it names assets and status codes, it is what a bug report
 * is asked for, and it stays English in both languages. The sentence above it
 * is ours and is translated; the line itself is drawn `dir="ltr"` so bidi
 * cannot walk a path or a hash out of order. The two never mix, which is why
 * there is no entry here that interpolates a detail into a sentence.
 *
 * The same boundary runs through `terminal`: the three words below are the
 * status the pane head shows, which is prose. What `SessionTerminal` writes
 * *into* the xterm buffer is not here and is not translated — it sits in a
 * `dir="ltr"` character grid beside real shell output, and Arabic in that
 * grid would be reordered into nonsense.
 *
 * The unit on a download size is not translated either, and `megabytes()` in
 * lib/format says why: `MB` is a symbol every browser's download indicator
 * already shows, and `م.ب` would be a puzzle. The number in front of it is
 * localised, the symbol is not.
 */
import { ltr, megabytes } from '../format';

export const en = {
  /* --- BootScreen.tsx, while it is working ------------------------------- */

  heading: 'Starting your workstation',
  lede:
    'EngineerOS runs a real Linux inside this tab. Your bash, your git and your files all live here, on your ' +
    'machine, and nothing you type is sent anywhere.',
  /** The progress bar's name, for a screen reader. */
  barLabel: 'Starting the Linux machine',
  /** `FIRST_PROGRESS.message`: the line before the VM has said anything. */
  firstMessage: 'Reading the build.',
  /** `12.4 MB of 120.0 MB`. One decimal, because two is noise at this size. */
  bytes: (loaded: number, total: number) => `${megabytes(loaded, 'en')} of ${megabytes(total, 'en')}`,

  /* The four steps, in the order they happen. `restore` and `boot` are one
     step: from outside they are the same wait. */
  steps: {
    manifest: 'Reading the build',
    assets: 'Downloading Linux',
    start: 'Starting the machine',
    warm: 'Warming up',
  },

  note:
    'The first visit downloads the machine once, about thirteen megabytes. After that it starts from your cache ' +
    'in a second or two.',

  /* --- BootScreen.tsx, when it has stopped -------------------------------- */

  failure: {
    heading: 'Could not start',
    retry: 'Try again',

    /* One sentence per `VmError.code`, and never a stack trace. */
    reason: {
      unsupported:
        'This browser cannot run the Linux machine EngineerOS needs. It wants WebAssembly and about half a gigabyte of free memory, which a current Chrome, Edge, Firefox or Safari on a laptop or desktop has. A phone or a tablet does not.',
      memory:
        'There is not enough free memory in this tab to start Linux. Closing some other tabs and reloading usually finds it; about half a gigabyte is needed.',
      manifest:
        'The description of the Linux machine could not be read. That normally means a deploy is half finished, and reloading in a minute picks up the whole of it.',
      fingerprint:
        'This page and the saved Linux machine came from different builds, so the machine will not be restored rather than being restored wrongly. Reloading picks up the matching pair; if it keeps happening, clear this site’s cache.',
      download:
        'Part of the Linux machine did not download. Check the connection and try again — nothing is lost, and what did arrive is already cached.',
      boot: 'Linux started but never answered. This is a bug in EngineerOS rather than anything you did. Try again, and if it happens twice, the build is broken.',
      timeout: 'The machine took too long to answer and was given up on. Try again.',
      destroyed: 'The machine was shut down while it was still starting. Try again.',
    },

    /** A code no sentence was written for. Says the same two things anyway. */
    fallback: 'EngineerOS could not start the Linux machine it runs on. Try again, and if it happens twice, the build is broken.',
  },

  /* --- terminal.ts -------------------------------------------------------- */

  /**
   * The page a phone gets instead of a boot (components/PhoneGate.tsx): the
   * machine needs a computer, so the useful thing is to carry the link to one.
   */
  phone: {
    title: 'You can start on this phone',
    body: 'Each level is a short task on a real Linux machine inside this tab; Level 0 takes about four minutes. On a phone you get the terminal with a row of extra keys, and Iris can do the typing while you decide what runs.',
    start: 'Start on this phone',
    startNote: 'The first start downloads about 13 MB and can take a minute on a phone.',
    share: 'Send the link to a computer',
    shareText: 'EngineerOS: open this on my computer',
    copy: 'Copy link',
    copied: 'Copied',
  },
  /**
   * The status the work pane shows beside the Terminal tab. Prose, so it is
   * translated; read with `getStrings()` rather than the hook, because
   * `SessionTerminal` is a class and not a component.
   */
  terminal: {
    connecting: 'connecting…',
    connected: 'connected',
    disconnected: 'disconnected',
  },

  /* --- engine/runtime.ts -------------------------------------------------- */

  /**
   * The two thrown messages a learner can actually read. Everything else
   * thrown in runtime.ts and api.ts names a URL, a schema or a session id and
   * is a developer's sentence; it stays English, where the person who has to
   * act on it will read it.
   */
  runtime: {
    /** The catalogue arrived and was empty. Shown as the boot failure detail. */
    noLevels: 'No levels are installed.',
    /** The last gate in front of a paid level. Shown as a toast on the play screen. */
    paidLevel: (missionId: string) =>
      `level "${missionId}" is part of the paid half of the course, which this browser has not been given`,
  },
};

export const ar: typeof en = {
  heading: 'نجهّز مساحة عملك',
  lede:
    'يشغّل EngineerOS نظام Linux حقيقيًا داخل هذا التبويب. ستجد bash و Git وملفاتك كلها هنا على جهازك، ' +
    'ولن يُرسل أي شيء تكتبه إلى الخارج.',
  barLabel: 'بدء بيئة Linux',
  firstMessage: 'قراءة بيانات الإصدار…',
  // `من` between two left-to-right runs: each number carries its own `MB`, so
  // bidi resolves them as two runs around one Arabic word and the larger
  // total stays on the left where the eye expects the destination.
  bytes: (loaded: number, total: number) => `${megabytes(loaded, 'ar')} من ${megabytes(total, 'ar')}`,

  steps: {
    manifest: 'قراءة بيانات الإصدار',
    assets: 'تنزيل Linux',
    start: 'تشغيل البيئة',
    warm: 'التجهيز',
  },

  note:
    'في زيارتك الأولى، سننزّل نحو 13 ميغابايت مرة واحدة. بعد ذلك، ستبدأ البيئة من الذاكرة المؤقتة خلال ثانية ' +
    'أو ثانيتين.',

  failure: {
    heading: 'تعذّر البدء',
    retry: 'حاول مرة أخرى',

    reason: {
      unsupported:
        'لا يستطيع هذا المتصفح تشغيل بيئة Linux التي يحتاجها EngineerOS. تحتاج إلى WebAssembly ونحو نصف غيغابايت من الذاكرة المتاحة. استخدم إصدارًا حديثًا من Chrome أو Edge أو Firefox أو Safari على حاسوب؛ فالهواتف والأجهزة اللوحية غير مدعومة.',
      memory:
        'لا تتوفر ذاكرة كافية لتشغيل Linux في هذا التبويب. أغلق بعض التبويبات الأخرى وأعد تحميل الصفحة؛ تحتاج البيئة إلى نحو نصف غيغابايت.',
      manifest:
        'تعذّرت قراءة بيانات بيئة Linux. يحدث هذا عادةً عندما لا يكتمل نشر الإصدار. أعد تحميل الصفحة بعد دقيقة.',
      fingerprint:
        'الصفحة وبيئة Linux المحفوظة تنتميان إلى إصدارين مختلفين، لذلك لم نستخدم النسخة القديمة. أعد تحميل الصفحة لتنزيل النسخة المطابقة. إذا تكرر الخطأ، امسح الذاكرة المؤقتة لهذا الموقع.',
      download:
        'لم يكتمل تنزيل أحد أجزاء بيئة Linux. تحقق من اتصالك وحاول مرة أخرى. لن تفقد شيئًا؛ فالأجزاء التي نُزّلت محفوظة في الذاكرة المؤقتة.',
      boot: 'بدأ Linux لكنه لم يستجب. هذا خلل في EngineerOS وليس بسبب ما فعلته. حاول مرة أخرى، وإذا تكرر الخطأ فالإصدار الحالي به مشكلة.',
      timeout: 'استغرقت بيئة Linux وقتًا أطول من المتوقع للاستجابة. حاول مرة أخرى.',
      destroyed: 'توقفت بيئة Linux أثناء بدء التشغيل. حاول مرة أخرى.',
    },

    fallback:
      'تعذّر على EngineerOS تشغيل بيئة Linux. حاول مرة أخرى، وإذا تكرر الخطأ فالإصدار الحالي به مشكلة.',
  },

  phone: {
    title: 'يمكنك أن تبدأ على هذا الهاتف',
    body: 'كل مستوى مهمة قصيرة على بيئة Linux حقيقية داخل هذه الصفحة، والمستوى 0 يستغرق نحو أربع دقائق. على الهاتف تحصل على الـ terminal مع صف من المفاتيح الإضافية، ويمكن لـ Iris أن تكتب الأوامر بينما تقرر أنت ما يُنفَّذ.',
    start: 'ابدأ على هذا الهاتف',
    startNote: 'أول تشغيل ينزّل نحو 13 ميغابايت وقد يستغرق دقيقة على الهاتف.',
    share: 'أرسل الرابط إلى حاسوب',
    shareText: 'EngineerOS: لأفتحه على حاسوبي',
    copy: 'انسخ الرابط',
    copied: 'نُسخ',
  },
  terminal: {
    connecting: 'جارٍ الاتصال…',
    connected: 'متصل',
    disconnected: 'انقطع الاتصال',
  },

  runtime: {
    noLevels: 'لا مستويات مثبَّتة.',
    // The id is a machine string in the middle of a sentence: isolated, or
    // bidi walks `l23-the-stale-branch` out of the quotes it sits in.
    paidLevel: (missionId: string) => `المستوى ${ltr(missionId)} ضمن الدورة الكاملة، وهي غير متاحة في هذا المتصفح`,
  },
};

export const de: typeof en = {
  heading: 'Dein Arbeitsplatz startet',
  lede: 'EngineerOS lässt ein echtes Linux in diesem Tab laufen. Deine bash, dein git und deine Dateien leben hier, auf deinem Rechner, und nichts, was du tippst, wird irgendwohin geschickt.',
  barLabel: 'Die Linux-Maschine startet',
  firstMessage: 'Build wird gelesen.',
  bytes: (loaded, total) => `${megabytes(loaded, 'de')} von ${megabytes(total, 'de')}`,
  steps: {
    manifest: 'Build wird gelesen',
    assets: 'Linux wird heruntergeladen',
    start: 'Maschine startet',
    warm: 'Aufwärmen',
  },
  note: 'Beim ersten Besuch wird die Maschine einmal heruntergeladen, etwa dreizehn Megabyte. Danach startet sie in ein, zwei Sekunden aus deinem Cache.',
  failure: {
    heading: 'Start fehlgeschlagen',
    retry: 'Erneut versuchen',
    reason: {
      unsupported: 'Dieser Browser kann die Linux-Maschine, die EngineerOS braucht, nicht ausführen. Sie braucht WebAssembly und etwa ein halbes Gigabyte freien Speicher – das hat ein aktuelles Chrome, Edge, Firefox oder Safari auf einem Laptop oder Desktop. Ein Handy oder Tablet nicht.',
      memory: 'In diesem Tab ist nicht genug Speicher frei, um Linux zu starten. Meist hilft es, ein paar andere Tabs zu schließen und neu zu laden; gebraucht wird etwa ein halbes Gigabyte.',
      manifest: 'Die Beschreibung der Linux-Maschine konnte nicht gelesen werden. Das heißt normalerweise, dass ein Deploy halb fertig ist; wenn du in einer Minute neu lädst, bekommst du ihn ganz.',
      fingerprint: 'Diese Seite und die gespeicherte Linux-Maschine stammen aus verschiedenen Builds, deshalb wird die Maschine lieber gar nicht wiederhergestellt als falsch. Neu laden holt das passende Paar; passiert es immer wieder, leere den Cache dieser Website.',
      download: 'Ein Teil der Linux-Maschine wurde nicht heruntergeladen. Prüf die Verbindung und versuch es noch einmal – nichts geht verloren, und was schon angekommen ist, liegt bereits im Cache.',
      boot: 'Linux ist gestartet, hat aber nie geantwortet. Das ist ein Fehler in EngineerOS, nicht etwas, das du getan hast. Versuch es noch einmal; passiert es zweimal, ist der Build kaputt.',
      timeout: 'Die Maschine hat zu lange nicht geantwortet, deshalb wurde abgebrochen. Versuch es noch einmal.',
      destroyed: 'Die Maschine wurde heruntergefahren, während sie noch startete. Versuch es noch einmal.',
    },
    fallback: 'EngineerOS konnte die Linux-Maschine, auf der es läuft, nicht starten. Versuch es noch einmal; passiert es zweimal, ist der Build kaputt.',
  },
  phone: {
    title: 'Du kannst auf diesem Handy anfangen',
    body: 'Jedes Level ist eine kurze Aufgabe auf einer echten Linux-Maschine in diesem Tab; Level 0 dauert etwa vier Minuten. Auf dem Handy bekommst du das Terminal mit einer Reihe zusätzlicher Tasten, und Iris kann tippen, während du entscheidest, was läuft.',
    start: 'Auf diesem Handy starten',
    startNote: 'Der erste Start lädt etwa 13 MB und kann auf dem Handy eine Minute dauern.',
    share: 'Link an einen Computer senden',
    shareText: 'EngineerOS: auf meinem Computer öffnen',
    copy: 'Link kopieren',
    copied: 'Kopiert',
  },
  terminal: {
    connecting: 'verbinde …',
    connected: 'verbunden',
    disconnected: 'getrennt',
  },
  runtime: {
    noLevels: 'Es sind keine Level installiert.',
    paidLevel: (missionId) => `Level "${missionId}" gehört zum bezahlten Teil des Kurses, und dieser Browser hat ihn nicht bekommen`,
  },
};

export const ja: typeof en = {
  heading: 'ワークステーションを起動しています',
  lede: 'EngineerOSは、このタブの中で本物のLinuxを動かします。bash、git、ファイルはすべてあなたのマシン上にあり、入力した内容がどこかに送信されることはありません。',
  barLabel: 'Linuxマシンを起動中',
  firstMessage: 'ビルドを読み込んでいます。',
  bytes: (loaded: number, total: number) => `${megabytes(loaded, 'ja')} / ${megabytes(total, 'ja')}`,
  steps: {
    manifest: 'ビルドを読み込み中',
    assets: 'Linuxをダウンロード中',
    start: 'マシンを起動中',
    warm: 'ウォームアップ中',
  },
  note: '初回はマシンを一度だけダウンロードします(約13MB)。2回目以降はキャッシュから、1、2秒で起動します。',
  failure: {
    heading: '起動できませんでした',
    retry: 'もう一度試す',
    reason: {
      unsupported: 'このブラウザーでは、EngineerOSが必要とするLinuxマシンを動かせません。WebAssemblyと約0.5GBの空きメモリが必要で、最新のChrome、Edge、Firefox、SafariをノートPCやデスクトップで使っていれば満たせます。スマートフォンやタブレットでは動きません。',
      memory: 'このタブにはLinuxを起動するだけの空きメモリがありません。ほかのタブをいくつか閉じて再読み込みすると、たいてい解決します。必要なのは約0.5GBです。',
      manifest: 'Linuxマシンの定義を読み込めませんでした。多くの場合、デプロイが途中の状態です。1分ほど待って再読み込みすると、すべて揃った状態で読み込まれます。',
      fingerprint: 'このページと保存済みのLinuxマシンは別のビルドのものです。間違った状態で復元しないよう、マシンは復元されません。再読み込みすると対応する組み合わせが読み込まれます。何度も起きる場合は、このサイトのキャッシュを消去してください。',
      download: 'Linuxマシンの一部をダウンロードできませんでした。接続を確認して、もう一度試してください。失われたものはなく、届いた分はすでにキャッシュされています。',
      boot: 'Linuxは起動しましたが、応答がありませんでした。あなたの操作が原因ではなく、EngineerOS側の不具合です。もう一度試してください。2回続けて起きる場合は、ビルドが壊れています。',
      timeout: 'マシンの応答に時間がかかりすぎたため、中断しました。もう一度試してください。',
      destroyed: 'マシンが起動中に停止されました。もう一度試してください。',
    },
    fallback: 'EngineerOSは、動作の土台となるLinuxマシンを起動できませんでした。もう一度試してください。2回続けて起きる場合は、ビルドが壊れています。',
  },
  phone: {
    title: 'このスマホで始められます',
    body: 'どのレベルも、このタブの中の本物のLinuxマシンで取り組む短い課題です。レベル0は約4分です。スマホでは、追加キーの列が付いたターミナルが使え、何を実行するかはあなたが決め、入力はIrisに任せることもできます。',
    start: 'このスマホで始める',
    startNote: '初回の起動では約13MBをダウンロードし、スマホでは1分ほどかかることがあります。',
    share: 'リンクをパソコンに送る',
    shareText: 'EngineerOS：パソコンで開く',
    copy: 'リンクをコピー',
    copied: 'コピーしました',
  },
  terminal: {
    connecting: '接続中…',
    connected: '接続済み',
    disconnected: '切断されました',
  },
  runtime: {
    noLevels: 'インストールされているレベルがありません。',
    paidLevel: (missionId: string) => `レベル "${missionId}" はコースの有料部分に含まれており、このブラウザーには付与されていません`,
  },
};
